import { useEffect, useState } from 'react'
import type { Attachment, DocumentReference } from '@medplum/fhirtypes'
import { useClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'

export type AuthorizationStatus = 'missing' | 'completed' | 'expired' | 'needs-signature'

// Provider-scoped read through the middleware oRPC (authorizationGet with a
// patientId — Practitioner-only, IDOR-guarded server-side). No direct medplum
// calls from the frontend, per Yurie's rule.
export function useAuthorizationStatus(patientId: string) {
  const client = useClient()
  // Status and signedDate are not meaningful until loading is false; callers must gate on `loading`.
  const [status, setStatus] = useState<AuthorizationStatus>('missing')
  const [signedDate, setSignedDate] = useState<string | undefined>(undefined)
  // Attachment of the signed authorization PDF — populated only when active.
  const [attachment, setAttachment] = useState<Attachment | undefined>(undefined)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!patientId) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    client
      .authorizationGet({ patientId })
      .then((res) => {
        if (cancelled) return
        const items = res.items ?? []
        // Patient-level rollup across orgs: any granted authorization → completed.
        const granted = items.find((item) => item.granted)
        if (granted) {
          setStatus('completed')
          setSignedDate(granted.signedDate)
          setAttachment(
            (granted.document as DocumentReference | undefined)?.content?.[0]?.attachment,
          )
          return
        }
        const rejected = items.find(
          (item) => item.status === 'rejected' || item.status === 'inactive',
        )
        setAttachment(undefined)
        setSignedDate(undefined)
        // proposed/draft (generated-but-unsigned) → 'missing' (not yet authorized).
        setStatus(rejected ? 'needs-signature' : 'missing')
      })
      .catch((err) => {
        logError('AuthorizationRelease', 'load authorization status', err)
        if (!cancelled) {
          setStatus('missing')
          setSignedDate(undefined)
          setAttachment(undefined)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [client, patientId])

  return { status, loading, signed: status === 'completed', signedDate, attachment }
}
